import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../api';
import {
  Plus, X, Check, Trash2, Link2, Paperclip, Tag,
  ExternalLink, FileText, Image, FileSpreadsheet, File, Upload, Edit3,
  Calendar, User, Circle, Clock, CheckCircle2, XCircle,
  List, LayoutGrid, CalendarRange, Users, ClipboardList, Search
} from 'lucide-react';

const PRIORITA = ['bassa', 'media', 'alta'];
const STATI_TASK = ['da_fare', 'in_corso', 'completata', 'annullata'];

const COLORI = [
  { id: null,     bg: 'transparent', border: 'var(--border2)' },
  { id: 'cyan',   bg: 'rgba(0,212,255,0.15)', border: '#00d4ff' },
  { id: 'purple', bg: 'rgba(168,85,247,0.15)', border: '#a855f7' },
  { id: 'green',  bg: 'rgba(16,185,129,0.15)', border: '#10b981' },
  { id: 'yellow', bg: 'rgba(245,158,11,0.15)', border: '#f59e0b' },
  { id: 'red',    bg: 'rgba(244,63,94,0.15)', border: '#f43f5e' },
];

const PRI_STYLE = {
  alta:  { background: 'var(--red-dim)',    color: 'var(--red)'    },
  media: { background: 'var(--yellow-dim)', color: 'var(--yellow)' },
  bassa: { background: 'var(--surface)',    color: 'var(--text-muted)' },
};

const STATO_ICON = {
  da_fare:   { icon: Circle,       color: 'var(--text-dim)'  },
  in_corso:  { icon: Clock,        color: 'var(--yellow)'    },
  completata:{ icon: CheckCircle2, color: 'var(--green)'     },
  annullata: { icon: XCircle,      color: 'var(--red)'       },
};

function getDays(pastDays = 7, futureDays = 14) {
  const days = [];
  const today = new Date();
  for (let i = -pastDays; i <= futureDays; i++) {
    const d = new Date(today); d.setDate(today.getDate() + i);
    days.push(d.toISOString().split('T')[0]);
  }
  return days;
}

function dayLabel(d) {
  const today = new Date().toISOString().split('T')[0];
  if (d === today) return 'Oggi';
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  if (d === tomorrow.toISOString().split('T')[0]) return 'Domani';
  return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
}

function fileIcon(type) {
  if (['jpg','jpeg','png','gif','webp'].includes(type)) return Image;
  if (['xls','xlsx','csv'].includes(type)) return FileSpreadsheet;
  if (type === 'pdf' || type === 'txt') return FileText;
  return File;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Favicon del dominio del link (servizio pubblico, lato client — niente backend/SSRF).
// Fallback all'icona Link2 se il favicon non carica.
function Favicon({ url, size = 14 }) {
  const [err, setErr] = useState(false);
  let host = '';
  try { host = new URL(url.startsWith('http') ? url : 'https://' + url).hostname; } catch { /* url non valido */ }
  if (err || !host) return <Link2 size={size - 1} color="var(--cyan)" style={{ flexShrink: 0 }} />;
  return (
    <img src={`https://www.google.com/s2/favicons?domain=${host}&sz=64`} alt="" width={size} height={size}
      onError={() => setErr(true)}
      style={{ flexShrink: 0, borderRadius: 3, objectFit: 'contain' }} />
  );
}

// ── Task Detail Panel ────────────────────────────────────────────────────────
function TaskPanel({ task, onClose, onUpdate, clienti, utenti, readOnly, isOperatore }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...task });
  const [newLink, setNewLink] = useState({ url: '', label: '' });
  const [newTag, setNewTag] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Auto-includi link pendente se l'utente non ha cliccato "+ Aggiungi"
      const finalLinks = [...(form.links || [])];
      if (newLink.url.trim()) {
        const u = newLink.url.startsWith('http') ? newLink.url : 'https://' + newLink.url;
        finalLinks.push({ url: u, label: newLink.label || u });
        setNewLink({ url: '', label: '' });
      }
      const res = await api.put(`/task/${task.id}`, { ...form, links: finalLinks });
      onUpdate(res.data);
      setEditing(false);
    } finally { setSaving(false); }
  };

  const addLink = () => {
    if (!newLink.url.trim()) return;
    const url = newLink.url.startsWith('http') ? newLink.url : 'https://' + newLink.url;
    set('links', [...(form.links || []), { url, label: newLink.label || url }]);
    setNewLink({ url: '', label: '' });
  };

  const removeLink = (i) => set('links', form.links.filter((_, idx) => idx !== i));

  const addTag = () => {
    if (!newTag.trim() || (form.tags || []).includes(newTag.trim())) return;
    set('tags', [...(form.tags || []), newTag.trim()]);
    setNewTag('');
  };

  const removeTag = (t) => set('tags', (form.tags || []).filter(x => x !== t));

  const handleUpload = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append('file', file);
        const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        set('allegati', [...(form.allegati || []), res.data]);
      }
    } finally { setUploading(false); }
  };

  const removeAllegato = async (a, i) => {
    await api.delete(`/upload/${a.filename}`).catch(() => {});
    set('allegati', (form.allegati || []).filter((_, idx) => idx !== i));
  };

  const isImg = (type) => ['jpg','jpeg','png','gif','webp'].includes(type);

  const colorCfg = COLORI.find(c => c.id === form.colore) || COLORI[0];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
    }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }} />

      {/* Panel */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: 'min(520px, 100vw)', height: '100vh',
        background: 'var(--bg2)',
        borderLeft: `2px solid ${colorCfg.border === 'var(--border2)' ? 'var(--border2)' : colorCfg.border}`,
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 10,
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!readOnly && (editing ? (
              <>
                <button onClick={save} disabled={saving} style={{
                  background: 'var(--cyan)', color: 'var(--on-accent)', border: 'none',
                  borderRadius: 6, padding: '6px 14px', fontSize: 12.5, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <Check size={13} /> {saving ? 'Salvo...' : 'Salva'}
                </button>
                <button onClick={() => { setForm({ ...task }); setEditing(false); }} style={{
                  background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                  color: 'var(--text-muted)', padding: '6px 12px', fontSize: 12.5, cursor: 'pointer',
                }}>Annulla</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 6, color: 'var(--text-muted)', padding: '6px 12px',
                fontSize: 12.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <Edit3 size={13} /> Modifica
              </button>
            ))}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '22px', flex: 1 }}>
          {/* Titolo */}
          {editing ? (
            <input
              value={form.titolo}
              onChange={e => set('titolo', e.target.value)}
              style={{
                width: '100%', background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text)', font: 'inherit',
                fontSize: 17, fontWeight: 600, padding: '10px 12px', outline: 'none', marginBottom: 16,
              }}
            />
          ) : (
            <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 16, lineHeight: 1.4 }}>
              {form.titolo}
            </h2>
          )}

          {/* Meta row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {/* Stato */}
            {editing ? (
              <select value={form.stato} onChange={e => set('stato', e.target.value)} style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
                color: 'var(--text)', font: 'inherit', fontSize: 12.5, padding: '5px 10px', cursor: 'pointer',
              }}>
                {STATI_TASK.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            ) : (() => {
              const S = STATO_ICON[form.stato] || STATO_ICON.da_fare;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--surface)', borderRadius: 6, padding: '4px 10px' }}>
                  <S.icon size={13} color={S.color} />
                  <span style={{ fontSize: 12.5, color: S.color }}>{form.stato?.replace('_',' ')}</span>
                </div>
              );
            })()}

            {/* Priorità */}
            {editing ? (
              <select value={form.priorita} onChange={e => set('priorita', e.target.value)} style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
                color: 'var(--text)', font: 'inherit', fontSize: 12.5, padding: '5px 10px', cursor: 'pointer',
              }}>
                {PRIORITA.map(p => <option key={p}>{p}</option>)}
              </select>
            ) : (
              <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 999, ...PRI_STYLE[form.priorita] }}>
                {form.priorita}
              </span>
            )}

            {/* Scadenza */}
            {editing ? (
              <input type="date" value={form.data_scadenza} onChange={e => set('data_scadenza', e.target.value)} style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
                color: 'var(--text)', font: 'inherit', fontSize: 12.5, padding: '5px 10px',
              }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--surface)', borderRadius: 6, padding: '4px 10px' }}>
                <Calendar size={12} color="var(--text-dim)" />
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{form.data_scadenza}</span>
              </div>
            )}
          </div>

          {/* Assegnato + Cliente */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }}>Assegnata a</label>
              {(editing && !isOperatore) ? (
                <select value={form.assegnata_a || ''} onChange={e => set('assegnata_a', e.target.value || null)} style={{
                  width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
                  color: 'var(--text)', font: 'inherit', fontSize: 12.5, padding: '6px 10px',
                }}>
                  <option value="">—</option>
                  {utenti.map(u => <option key={u.id} value={u.id}>{u.nome} {u.cognome}</option>)}
                </select>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <User size={13} color="var(--text-dim)" />
                  <span style={{ fontSize: 13, color: form.assegnata_nome ? 'var(--text)' : 'var(--text-dim)' }}>
                    {form.assegnata_nome || 'Non assegnata'}
                  </span>
                </div>
              )}
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }}>Cliente</label>
              {editing ? (
                <select value={form.cliente_id || ''} onChange={e => set('cliente_id', e.target.value || null)} style={{
                  width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
                  color: 'var(--text)', font: 'inherit', fontSize: 12.5, padding: '6px 10px',
                }}>
                  <option value="">—</option>
                  {clienti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome || ''}</option>)}
                </select>
              ) : (
                <span style={{ fontSize: 13, color: form.cliente_nome ? 'var(--text)' : 'var(--text-dim)' }}>
                  {form.cliente_nome || '—'}
                </span>
              )}
            </div>
          </div>

          {/* Colore etichetta */}
          {editing && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Colore etichetta</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {COLORI.map(c => (
                  <button key={c.id || 'none'} onClick={() => set('colore', c.id)} style={{
                    width: 24, height: 24, borderRadius: '50%', border: `2px solid ${c.border}`,
                    background: c.bg, cursor: 'pointer',
                    outline: form.colore === c.id ? `2px solid ${c.border}` : 'none',
                    outlineOffset: 2,
                  }} />
                ))}
              </div>
            </div>
          )}

          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0 20px' }} />

          {/* Descrizione */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Descrizione</label>
            {editing ? (
              <textarea
                value={form.descrizione || ''}
                onChange={e => set('descrizione', e.target.value)}
                rows={5}
                placeholder="Descrizione dettagliata, contesto, istruzioni..."
                style={{
                  width: '100%', background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 8, color: 'var(--text)', font: 'inherit', fontSize: 13.5,
                  padding: '12px', outline: 'none', resize: 'vertical', lineHeight: 1.7,
                }}
              />
            ) : form.descrizione ? (
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                padding: '14px', fontSize: 13.5, color: 'var(--text)', lineHeight: 1.75,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {form.descrizione}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-dim)', fontStyle: 'italic' }}>Nessuna descrizione</p>
            )}
          </div>

          {/* Tags */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Tag size={12} /> Tag
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: editing ? 8 : 0 }}>
              {(form.tags || []).map(t => (
                <div key={t} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: 'var(--purple-dim)', border: '1px solid rgba(168,85,247,0.25)',
                  borderRadius: 999, padding: '3px 10px', fontSize: 12, color: 'var(--purple)',
                }}>
                  {t}
                  {editing && (
                    <button onClick={() => removeTag(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--purple)', padding: 0, display: 'flex' }}>
                      <X size={11} />
                    </button>
                  )}
                </div>
              ))}
              {!editing && !(form.tags || []).length && <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>—</span>}
            </div>
            {editing && (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={newTag} onChange={e => setNewTag(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTag()}
                  placeholder="Aggiungi tag..."
                  style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', font: 'inherit', fontSize: 12.5, padding: '6px 10px', outline: 'none' }}
                />
                <button onClick={addTag} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer', padding: '6px 10px', display: 'flex' }}>
                  <Plus size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Links */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Link2 size={12} /> Link e riferimenti
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: editing ? 8 : 0 }}>
              {(form.links || []).map((l, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '9px 12px',
                }}>
                  <Favicon url={l.url} size={15} />
                  <a href={l.url} target="_blank" rel="noopener noreferrer" style={{
                    flex: 1, color: 'var(--cyan)', fontSize: 13, textDecoration: 'none',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {l.label || l.url}
                  </a>
                  <ExternalLink size={12} color="var(--text-dim)" />
                  {editing && (
                    <button onClick={() => removeLink(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, display: 'flex' }}>
                      <X size={13} />
                    </button>
                  )}
                </div>
              ))}
              {!editing && !(form.links || []).length && <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>—</span>}
            </div>
            {editing && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input
                  value={newLink.label} onChange={e => setNewLink(l => ({ ...l, label: e.target.value }))}
                  placeholder="Etichetta (es. Proposta commerciale)"
                  style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', font: 'inherit', fontSize: 12.5, padding: '7px 10px', outline: 'none' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={newLink.url} onChange={e => setNewLink(l => ({ ...l, url: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && addLink()}
                    placeholder="https://..."
                    style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', font: 'inherit', fontSize: 12.5, padding: '7px 10px', outline: 'none' }}
                  />
                  <button onClick={addLink} style={{ background: 'var(--cyan)', color: 'var(--on-accent)', border: 'none', borderRadius: 6, cursor: 'pointer', padding: '7px 12px', display: 'flex', fontWeight: 600, fontSize: 12.5 }}>
                    Aggiungi
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Allegati */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Paperclip size={12} /> Allegati
            </label>

            {/* Immagini in griglia */}
            {(form.allegati || []).filter(a => isImg(a.type)).length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 8 }}>
                {(form.allegati || []).filter(a => isImg(a.type)).map((a, i) => (
                  <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
                    <img src={a.url} alt={a.original} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    {editing && (
                      <button onClick={() => removeAllegato(a, (form.allegati || []).indexOf(a))} style={{
                        position: 'absolute', top: 4, right: 4,
                        background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%',
                        color: '#fff', cursor: 'pointer', width: 22, height: 22,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <X size={11} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* File non-immagine */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {(form.allegati || []).filter(a => !isImg(a.type)).map((a, i) => {
                const FIcon = fileIcon(a.type);
                const realIdx = (form.allegati || []).indexOf(a);
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 8, padding: '9px 12px',
                  }}>
                    <FIcon size={15} color="var(--text-dim)" style={{ flexShrink: 0 }} />
                    <a href={a.url} target="_blank" rel="noopener noreferrer" style={{
                      flex: 1, color: 'var(--text)', fontSize: 12.5, textDecoration: 'none',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {a.original}
                    </a>
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', flexShrink: 0 }}>{formatSize(a.size)}</span>
                    {editing && (
                      <button onClick={() => removeAllegato(a, realIdx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, display: 'flex' }}>
                        <X size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
              {!editing && !(form.allegati || []).length && <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>—</span>}
            </div>

            {editing && (
              <>
                <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
                  onChange={e => handleUpload(Array.from(e.target.files))} />
                <button onClick={() => fileRef.current.click()} disabled={uploading} style={{
                  marginTop: 10, width: '100%', background: 'var(--surface)',
                  border: '1px dashed var(--border2)', borderRadius: 8,
                  color: uploading ? 'var(--text-dim)' : 'var(--text-muted)',
                  cursor: 'pointer', padding: '10px', fontSize: 12.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  transition: 'border-color 0.15s',
                }}>
                  <Upload size={14} /> {uploading ? 'Caricamento...' : 'Carica file o immagini'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Create Task Form ──────────────────────────────────────────────────────────
function CreateTaskForm({ form, setForm, clienti, utenti, onSubmit, onClose, loading, isOperatore }) {
  const [newLink, setNewLink] = useState({ url: '', label: '' });
  const [newTag, setNewTag] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRefCreate = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addLink = () => {
    if (!newLink.url.trim()) return;
    const url = newLink.url.startsWith('http') ? newLink.url : 'https://' + newLink.url;
    set('links', [...(form.links || []), { url, label: newLink.label || url }]);
    setNewLink({ url: '', label: '' });
  };

  // Wrapper submit: auto-include link pendente se l'utente non ha premuto "+ Aggiungi"
  const handleSubmit = (e) => {
    if (newLink.url.trim()) {
      const url = newLink.url.startsWith('http') ? newLink.url : 'https://' + newLink.url;
      const extraLink = { url, label: newLink.label || url };
      const finalLinks = [...(form.links || []), extraLink];
      // Passa links finali come override al submit del parent
      onSubmit(e, finalLinks);
    } else {
      onSubmit(e, form.links || []);
    }
  };

  const addTag = () => {
    const t = newTag.trim();
    if (!t || (form.tags || []).includes(t)) return;
    set('tags', [...(form.tags || []), t]);
    setNewTag('');
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('allegati', [...(form.allegati || []), res.data]);
    } catch (err) {
      alert('Errore upload: ' + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); e.target.value = ''; }
  };

  const removeAllegato = async (a, idx) => {
    await api.delete(`/upload/${a.filename}`).catch(() => {});
    set('allegati', (form.allegati || []).filter((_, i) => i !== idx));
  };

  const isImg = (type) => type && type.startsWith('image/');

  return (
    <div className="eden-card fade-up" style={{ padding: 20, marginBottom: 16, border: '1px solid rgba(0,212,255,0.15)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>Nuova task</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', display: 'flex' }}><X size={15} /></button>
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Titolo */}
        <input className="eden-input" value={form.titolo}
          onChange={e => set('titolo', e.target.value)}
          required placeholder="Titolo della task *" />

        {/* Descrizione */}
        <textarea className="eden-input" value={form.descrizione} rows={3}
          onChange={e => set('descrizione', e.target.value)}
          placeholder="Descrizione, contesto, istruzioni..." style={{ resize: 'vertical' }} />

        {/* Meta row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 8 }}>
          <input type="date" className="eden-input" value={form.data_scadenza}
            onChange={e => set('data_scadenza', e.target.value)} />
          <select className="eden-select" value={form.priorita}
            onChange={e => set('priorita', e.target.value)} style={{ width: '100%' }}>
            {PRIORITA.map(p => <option key={p}>{p}</option>)}
          </select>
          {!isOperatore && (
            <select className="eden-select" value={form.assegnata_a}
              onChange={e => set('assegnata_a', e.target.value)} style={{ width: '100%' }}>
              <option value="">Non assegnata</option>
              {utenti.map(u => <option key={u.id} value={u.id}>{u.nome} {u.cognome}</option>)}
            </select>
          )}
          <select className="eden-select" value={form.cliente_id}
            onChange={e => set('cliente_id', e.target.value)} style={{ width: '100%' }}>
            <option value="">Nessun cliente</option>
            {clienti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome || ''}</option>)}
          </select>
        </div>

        <div style={{ height: 1, background: 'var(--border)' }} />

        {/* Link */}
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
            <Link2 size={12} /> Link e riferimenti
          </label>

          {/* Lista link aggiunti */}
          {(form.links || []).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8 }}>
              {(form.links || []).map((l, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '7px 10px' }}>
                  <Favicon url={l.url} size={14} />
                  <span style={{ flex: 1, fontSize: 12.5, color: 'var(--cyan)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.label || l.url}
                  </span>
                  <button type="button" onClick={() => set('links', form.links.filter((_, idx) => idx !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, display: 'flex' }}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input nuovo link */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input className="eden-input" value={newLink.label}
              onChange={e => setNewLink(l => ({ ...l, label: e.target.value }))}
              placeholder="Etichetta (es. Proposta commerciale)" style={{ fontSize: 12.5 }} />
            <div style={{ display: 'flex', gap: 7 }}>
              <input className="eden-input" value={newLink.url}
                onChange={e => setNewLink(l => ({ ...l, url: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
                placeholder="https://..." style={{ flex: 1, fontSize: 12.5 }} />
              <button type="button" onClick={addLink} className="eden-btn-ghost" style={{ flexShrink: 0, padding: '7px 12px', fontSize: 12.5 }}>
                + Aggiungi
              </button>
            </div>
          </div>
        </div>

        {/* Tag */}
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
            <Tag size={12} /> Tag
          </label>
          {(form.tags || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 7 }}>
              {(form.tags || []).map(t => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--purple-dim)', border: '1px solid rgba(168,85,247,0.25)', borderRadius: 999, padding: '3px 9px', fontSize: 12, color: 'var(--purple)' }}>
                  {t}
                  <button type="button" onClick={() => set('tags', form.tags.filter(x => x !== t))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--purple)', padding: 0, display: 'flex' }}>
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 7 }}>
            <input className="eden-input" value={newTag}
              onChange={e => setNewTag(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              placeholder="Aggiungi tag e premi Invio..." style={{ flex: 1, fontSize: 12.5 }} />
            <button type="button" onClick={addTag} className="eden-btn-ghost" style={{ flexShrink: 0, padding: '7px 12px', fontSize: 12.5 }}>
              + Tag
            </button>
          </div>
        </div>

        {/* Allegati */}
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
            <Paperclip size={12} /> Foto e allegati
          </label>
          <input ref={fileRefCreate} type="file" style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            onChange={uploadFile} />
          {/* Anteprime immagini */}
          {(form.allegati || []).filter(a => isImg(a.type)).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 8 }}>
              {(form.allegati || []).filter(a => isImg(a.type)).map((a, i) => (
                <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', aspectRatio: '1', background: 'var(--surface)', border: '1px solid var(--border)' }}>
                  <img src={a.url} alt={a.original} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button type="button" onClick={() => removeAllegato(a, (form.allegati || []).indexOf(a))}
                    style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* File non immagine */}
          {(form.allegati || []).filter(a => !isImg(a.type)).map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px', marginBottom: 5 }}>
              <FileText size={12} color="var(--text-dim)" />
              <span style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.original}</span>
              <button type="button" onClick={() => removeAllegato(a, (form.allegati || []).indexOf(a))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, display: 'flex' }}>
                <X size={12} />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => fileRefCreate.current?.click()} disabled={uploading}
            className="eden-btn-ghost" style={{ width: '100%', justifyContent: 'center', gap: 8, padding: '8px', fontSize: 12.5, color: uploading ? 'var(--text-dim)' : 'var(--text-muted)' }}>
            <Upload size={13} />
            {uploading ? 'Caricamento...' : 'Carica foto o file'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
          <button type="submit" disabled={loading || uploading} className="eden-btn-primary">
            {loading ? 'Salvataggio...' : 'Aggiungi task'}
          </button>
          <button type="button" onClick={onClose} className="eden-btn-ghost">Annulla</button>
        </div>
      </form>
    </div>
  );
}

// ── Task List Component ──────────────────────────────────────────────────────
const PRI_DOT = { alta: 'var(--red)', media: 'var(--yellow)', bassa: 'var(--text-dim)' };

function TaskCard({ t, isOperatore, colorBorder, onSelect, onToggle, onDelete }) {
  const S = STATO_ICON[t.stato] || STATO_ICON.da_fare;
  const done = t.stato === 'completata';
  const accent = colorBorder(t.colore);
  const hasAccent = accent !== 'var(--border)';
  return (
    <div
      key={t.id}
      onClick={() => onSelect(t)}
      className="fade-up"
      style={{
        position:'relative', padding:'14px 16px 14px 18px',
        display:'flex', alignItems:'flex-start', gap:13, cursor:'pointer',
        background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12,
        opacity:t.stato==='annullata'?0.5:1, transition:'background 0.14s, border-color 0.14s',
        overflow:'hidden', touchAction:'manipulation',
      }}
      onMouseEnter={e => { e.currentTarget.style.background='var(--surface-h)'; e.currentTarget.style.borderColor='var(--border-h)'; }}
      onMouseLeave={e => { e.currentTarget.style.background='var(--surface)'; e.currentTarget.style.borderColor='var(--border)'; }}
    >
      {/* Accent stripe (solo se il task ha un colore etichetta) */}
      {hasAccent && <span style={{ position:'absolute', left:0, top:0, bottom:0, width:3, background:accent }} />}

      <button onClick={e => onToggle(t,e)} title={done?'Segna da fare':'Segna completata'}
        aria-label={done?'Segna da fare':'Segna completata'}
        style={{ width:21, height:21, minWidth:21, minHeight:21, padding:0, boxSizing:'border-box', appearance:'none',
          borderRadius:'50%', flexShrink:0, marginTop:1,
          border:done?'none':'1.5px solid var(--border-h)', background:done?'var(--green)':'transparent',
          cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s', touchAction:'manipulation' }}
        onMouseEnter={e=>{ if(!done) e.currentTarget.style.borderColor='var(--green)'; }}
        onMouseLeave={e=>{ if(!done) e.currentTarget.style.borderColor='var(--border-h)'; }}>
        {done && <Check size={12} color="var(--on-accent)" strokeWidth={3}/>}
      </button>

      <div style={{ flex:1, minWidth:0 }}>
        {/* Titolo + indicatori */}
        <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:7 }}>
          <p style={{ fontSize:14, fontWeight:600, color:done?'var(--text-dim)':'var(--text)', textDecoration:done?'line-through':'none', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', margin:0, lineHeight:1.3 }}>{t.titolo}</p>
          {(t.links||[]).length>0 && <Link2 size={11.5} color="var(--cyan)" style={{flexShrink:0}}/>}
          {(t.allegati||[]).length>0 && <Paperclip size={11.5} color="var(--text-dim)" style={{flexShrink:0}}/>}
        </div>

        {/* Meta row */}
        <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
          {/* Priorità: dot + label */}
          <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600, color:PRI_DOT[t.priorita]||'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:PRI_DOT[t.priorita]||'var(--text-dim)' }}/>
            {t.priorita}
          </span>
          {/* Stato */}
          <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11.5, color:S.color }}>
            <S.icon size={12} color={S.color}/> {t.stato?.replace('_',' ')}
          </span>
          {/* Assegnato */}
          {t.assegnata_nome && (
            <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11.5, color:'var(--text-muted)' }}>
              <User size={11} color="var(--text-dim)"/> {t.assegnata_nome}
            </span>
          )}
          {/* Tag inline */}
          {(t.tags||[]).slice(0,3).map(tag=>(
            <span key={tag} style={{ fontSize:10.5, padding:'2px 8px', borderRadius:999, background:'var(--purple-dim)', color:'var(--purple)', border:'1px solid rgba(168,85,247,0.2)', fontWeight:500 }}>{tag}</span>
          ))}
          {(t.tags||[]).length>3 && <span style={{ fontSize:10.5, color:'var(--text-dim)' }}>+{(t.tags||[]).length-3}</span>}
        </div>
      </div>

      {/* Right cluster: cliente chip + delete */}
      <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0, alignSelf:'center' }}>
        {t.cliente_nome && (
          <span style={{ fontSize:11.5, color:'var(--text-muted)', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7, padding:'4px 10px', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {t.cliente_nome}
          </span>
        )}
        {!isOperatore && (
          <button onClick={e=>onDelete(t.id,e)}
            style={{ background:'none', border:'none', color:'var(--text-dim)', cursor:'pointer', padding:6, display:'flex', borderRadius:7, transition:'color 0.14s, background 0.14s' }}
            onMouseEnter={e=>{e.currentTarget.style.color='var(--red)'; e.currentTarget.style.background='var(--red-dim)';}}
            onMouseLeave={e=>{e.currentTarget.style.color='var(--text-dim)'; e.currentTarget.style.background='none';}}
            title="Elimina task">
            <Trash2 size={14}/>
          </button>
        )}
      </div>
    </div>
  );
}

function TaskList({ tasks, vistatutte, selectedDay, isOperatore, colorBorder, onSelect, onToggle, onDelete, totalTasks }) {
  if (tasks.length === 0) {
    return (
      <div className="eden-card" style={{ padding:'48px 40px', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
        <div style={{ width:48, height:48, borderRadius:14, background:'var(--surface)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <CheckCircle2 size={22} color="var(--text-dim)" />
        </div>
        <p style={{ fontSize:14, color:'var(--text-muted)', margin:0 }}>
          {vistatutte ? 'Nessuna task' : `Nessuna task per ${dayLabel(selectedDay)}`}
        </p>
        {!vistatutte && totalTasks > 0 && (
          <p style={{ fontSize:12.5, color:'var(--text-dim)', margin:0, display:'inline-flex', alignItems:'center', gap:5, flexWrap:'wrap', justifyContent:'center' }}>
            Ci sono task in altri giorni — usa
            <span style={{ display:'inline-flex', alignItems:'center', gap:4, color:'var(--purple)', fontWeight:600 }}>
              <ClipboardList size={13} /> Tutte
            </span>
            per vederle.
          </p>
        )}
      </div>
    );
  }
  if (vistatutte) {
    const grouped = {};
    tasks.forEach(t => { const k = String(t.data_scadenza); if (!grouped[k]) grouped[k]=[]; grouped[k].push(t); });
    return (
      <div>
        {Object.keys(grouped).sort().map(date => (
          <div key={date} style={{ marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', padding:'6px 0 8px', borderBottom:'1px solid var(--border)', marginBottom:8 }}>
              {dayLabel(date)} <span style={{ color:'var(--text-dim)', fontWeight:400 }}>· {grouped[date].length} task</span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
              {grouped[date].map(t => <TaskCard key={t.id} t={t} isOperatore={isOperatore} colorBorder={colorBorder} onSelect={onSelect} onToggle={onToggle} onDelete={onDelete}/>)}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
      {tasks.map(t => <TaskCard key={t.id} t={t} isOperatore={isOperatore} colorBorder={colorBorder} onSelect={onSelect} onToggle={onToggle} onDelete={onDelete}/>)}
    </div>
  );
}

// ── Kanban Board ─────────────────────────────────────────────────────────────
const KANBAN_COLS = [
  { key:'da_fare',    label:'Da fare',   color:'var(--text-muted)', bg:'var(--surface)' },
  { key:'in_corso',  label:'In corso',  color:'var(--yellow)',      bg:'var(--yellow-dim)' },
  { key:'completata',label:'Completata', color:'var(--green)',      bg:'var(--green-dim)' },
  { key:'annullata', label:'Annullata',  color:'var(--red)',        bg:'var(--red-dim)' },
];

function KanbanBoard({ tasks, onSelect, onToggle, onDelete, colorBorder, isOperatore }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12, alignItems:'start' }}>
      {KANBAN_COLS.map(col => {
        const colTasks = tasks.filter(t => t.stato === col.key);
        return (
          <div key={col.key}>
            <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:10 }}>
              <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', color:col.color, background:col.bg, padding:'2px 9px', borderRadius:6 }}>
                {col.label}
              </span>
              <span style={{ fontSize:11, color:'var(--text-dim)' }}>{colTasks.length}</span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, minHeight:60 }}>
              {colTasks.map(t => (
                <div key={t.id} onClick={() => onSelect(t)}
                  style={{
                    background:'var(--surface)', border:`1px solid ${colorBorder(t.colore)}`,
                    borderRadius:10, padding:'11px 13px', cursor:'pointer',
                    opacity: t.stato==='annullata' ? 0.5 : 1,
                    transition:'background 0.15s, border-color 0.15s', touchAction:'manipulation',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--surface-h)'}
                  onMouseLeave={e => e.currentTarget.style.background='var(--surface)'}>
                  <div style={{ fontSize:13, fontWeight:500, color: t.stato==='completata'?'var(--text-dim)':'var(--text)', textDecoration: t.stato==='completata'?'line-through':'none', marginBottom:5, lineHeight:1.4 }}>{t.titolo}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    {t.priorita && t.priorita !== 'bassa' && (
                      <span style={{ fontSize:10, padding:'1px 7px', borderRadius:5, background: t.priorita==='alta'?'var(--red-dim)':'var(--yellow-dim)', color: t.priorita==='alta'?'var(--red)':'var(--yellow)', fontWeight:600 }}>
                        {t.priorita}
                      </span>
                    )}
                    {t.data_scadenza && (
                      <span style={{ fontSize:10.5, color:'var(--text-dim)' }}>
                        {new Date(t.data_scadenza+'T00:00:00').toLocaleDateString('it-IT',{day:'numeric',month:'short'})}
                      </span>
                    )}
                    {t.assegnata_nome && (
                      <span style={{ fontSize:10.5, color:'var(--text-dim)', marginLeft:'auto', display:'inline-flex', alignItems:'center', gap:3 }}>
                        <User size={10} /> {t.assegnata_nome.split(' ')[0]}
                      </span>
                    )}
                  </div>
                  {t.cliente_nome && (
                    <div style={{ fontSize:10.5, color:'var(--text-dim)', marginTop:4, borderTop:'1px solid var(--border)', paddingTop:4 }}>{t.cliente_nome}</div>
                  )}
                </div>
              ))}
              {colTasks.length === 0 && (
                <div style={{ fontSize:12, color:'var(--text-dim)', textAlign:'center', padding:'16px 0', border:'1px dashed var(--border)', borderRadius:10 }}>—</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Toolbar toggle (icon + label, two states) ────────────────────────────────
function ToolbarToggle({ active, onClick, activeColor = 'var(--cyan)', activeDim = 'var(--cyan-dim)', icon: Icon, label, title }) {
  return (
    <button onClick={onClick} title={title}
      style={{
        padding: '0 13px', height: 38, borderRadius: 9, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
        border: active ? `1px solid ${activeColor}` : '1px solid var(--border)',
        background: active ? activeDim : 'var(--surface)',
        color: active ? activeColor : 'var(--text-muted)',
        display: 'flex', alignItems: 'center', gap: 6, touchAction: 'manipulation',
        transition: 'background 0.15s, border-color 0.15s, color 0.15s', whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = 'var(--border-h)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = 'var(--border)'; }}>
      <Icon size={14} /> {label}
    </button>
  );
}

// ── Main Task Page ────────────────────────────────────────────────────────────
export default function Task() {
  const { user } = useAuth();
  const isOperatore = ['operatore', 'social_manager', 'videomaker'].includes(user?.ruolo);
  const { success } = useToast();
  const [tasks, setTasks] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date().toISOString().split('T')[0]);
  const [showForm, setShowForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({
    titolo:'', descrizione:'', data_scadenza: new Date().toISOString().split('T')[0],
    priorita:'media', stato:'da_fare', cliente_id:'', assegnata_a:'',
    links:[], allegati:[], tags:[], colore: null,
  });
  const [loadingForm, setLoadingForm] = useState(false);
  const [soloMie, setSoloMie] = useState(false);
  const [fStato, setFStato] = useState('');     // filtro stato
  const [fPriorita, setFPriorita] = useState(''); // filtro priorità
  const [fTesto, setFTesto] = useState('');      // ricerca testuale
  const [vistatutte, setVistaTutte] = useState(false);
  const [vistaKanban, setVistaKanban] = useState(false);
  const days = getDays(7, 14);
  const todayStr = new Date().toISOString().split('T')[0];

  const load = useCallback(async () => {
    const [t, c, u] = await Promise.all([
      api.get('/task'),
      api.get('/clienti?limit=200'),
      api.get('/admin/utenti').catch(() => ({ data: [] })),
    ]);
    const taskList = Array.isArray(t.data) ? t.data : [];
    setTasks(taskList);
    setClienti(c.data);
    setUtenti((u.data || []).filter(u => u.ruolo !== 'agente'));
    // Auto-seleziona il giorno più recente con task (o oggi se ci sono task oggi)
    setSelectedDay(prev => {
      const hasTodayTasks = taskList.some(t => t.data_scadenza === todayStr);
      if (hasTodayTasks) return todayStr;
      // Trova il giorno con task più vicino (preferibilmente nel passato recente)
      const daysWithTasks = [...new Set(taskList.map(t => String(t.data_scadenza)))].sort();
      if (!daysWithTasks.length) return prev;
      // Preferisci il giorno più recente ≤ oggi
      const past = daysWithTasks.filter(d => d <= todayStr);
      if (past.length) return past[past.length - 1];
      return daysWithTasks[0];
    });
  }, [todayStr]);

  useEffect(() => { load(); }, [load]);

  // Auto-apri task panel se ?open=<id> è nell'URL (da notifica o log)
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || !tasks.length) return;
    const task = tasks.find(t => t.id === openId);
    if (task) {
      setSelectedTask(task);
      setSearchParams({}, { replace: true }); // Pulisce l'URL
    }
  }, [searchParams, tasks, setSearchParams]);

  // Apri il form se ?new=1 (da Quick-add globale)
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setShowForm(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const baseTasks = soloMie ? tasks.filter(t => t.assegnata_a === user?.id || t.assegnata_a === String(user?.id)) : tasks;
  const applyFilters = (list) => list.filter(t => {
    if (fStato && t.stato !== fStato) return false;
    if (fPriorita && t.priorita !== fPriorita) return false;
    if (fTesto) {
      const q = fTesto.toLowerCase();
      const hay = `${t.titolo || ''} ${t.cliente_nome || ''} ${(t.tags || []).join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const visibleTasks = applyFilters(baseTasks);
  const dayTasks = vistatutte ? visibleTasks : visibleTasks.filter(t => String(t.data_scadenza) === selectedDay);
  const filtriAttivi = fStato || fPriorita || fTesto;
  // conteggi per i chip (sul set base, ignorando il filtro stesso)
  const countStato = (s) => baseTasks.filter(t => t.stato === s).length;

  const toggleStato = async (task, e) => {
    e.stopPropagation();
    const next = task.stato === 'completata' ? 'da_fare' : 'completata';
    await api.put(`/task/${task.id}`, { stato: next });
    success(next === 'completata' ? 'Task completata' : 'Task riaperta');
    load();
  };

  const submit = async (e, finalLinks) => {
    e.preventDefault(); setLoadingForm(true);
    try {
      await api.post('/task', {
        ...form,
        links: finalLinks !== undefined ? finalLinks : (form.links || []),
        cliente_id: form.cliente_id || null,
        assegnata_a: form.assegnata_a || null,
      });
      load(); setShowForm(false);
      success('Task creata');
      setForm({ titolo:'', descrizione:'', data_scadenza: selectedDay, priorita:'media', stato:'da_fare', cliente_id:'', assegnata_a:'', links:[], allegati:[], tags:[], colore: null });
    } finally { setLoadingForm(false); }
  };

  const deleteTask = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questa task?')) return;
    await api.delete(`/task/${id}`);
    success('Task eliminata');
    load();
  };

  const handleUpdate = (updated) => {
    setTasks(ts => ts.map(t => t.id === updated.id ? { ...t, ...updated } : t));
    setSelectedTask(t => t ? { ...t, ...updated } : null);
  };

  const colorBorder = (colore) => {
    const c = COLORI.find(x => x.id === colore);
    return c && c.id ? c.border : 'var(--border)';
  };

  return (
    <div className="eden-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap:'wrap', gap:12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Task {vistaKanban ? '— Kanban' : 'giornaliere'}</h1>
        <div className="eden-scroll-row" style={{ gap: 8, alignItems: 'center', maxWidth: '100%' }}>
          {/* Vista kanban toggle */}
          <ToolbarToggle
            active={vistaKanban}
            onClick={() => setVistaKanban(v => !v)}
            icon={vistaKanban ? List : LayoutGrid}
            label={vistaKanban ? 'Lista' : 'Kanban'}
            title={vistaKanban ? 'Passa alla vista lista' : 'Passa alla vista Kanban'}
          />
          {!vistaKanban && (
            <ToolbarToggle
              active={vistatutte}
              onClick={() => setVistaTutte(v => !v)}
              activeColor="var(--purple)" activeDim="var(--purple-dim)"
              icon={vistatutte ? ClipboardList : CalendarRange}
              label={vistatutte ? 'Tutte' : 'Giorno'}
              title={vistatutte ? 'Mostra per singolo giorno' : 'Mostra tutte le date'}
            />
          )}
          {!isOperatore && (
            <ToolbarToggle
              active={soloMie}
              onClick={() => setSoloMie(v => !v)}
              activeColor="var(--green)" activeDim="var(--green-dim)"
              icon={soloMie ? User : Users}
              label={soloMie ? 'Mie' : 'Tutte'}
              title={soloMie ? 'Mostra le task di tutti' : 'Mostra solo le mie task'}
            />
          )}
          <button className="eden-btn-primary" onClick={() => setShowForm(s => !s)} style={{ height: 38 }}>
            <Plus size={15} /> Nuova task
          </button>
        </div>
      </div>

      {/* Barra filtri: ricerca + chip stato/priorità */}
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:16 }}>
        <div style={{ position:'relative', flex:'1 1 260px', minWidth:0 }}>
          <Search size={14} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'var(--text-dim)' }}/>
          <input value={fTesto} onChange={e => setFTesto(e.target.value)} placeholder="Cerca per titolo, cliente o tag…"
            className="eden-input" style={{ paddingLeft:34, height:36, fontSize:12.5 }}/>
        </div>
        {/* Chip: una sola riga scorrevole (su mobile non vanno più a capo) */}
        <div className="eden-scroll-row" style={{ flex:'0 1 auto', minWidth:0, maxWidth:'100%', alignItems:'center' }}>
        {STATI_TASK.map(s => {
          const SI = STATO_ICON[s] || STATO_ICON.da_fare; const active = fStato === s; const n = countStato(s);
          return (
            <button key={s} onClick={() => setFStato(active ? '' : s)} title={`Filtra: ${s.replace('_',' ')}`}
              style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11.5, fontWeight:600, padding:'6px 10px', borderRadius:999, cursor:'pointer', touchAction:'manipulation',
                border:`1px solid ${active ? SI.color : 'var(--border)'}`,
                background: active ? 'var(--surface-h)' : 'var(--surface)',
                color: active ? SI.color : 'var(--text-muted)' }}>
              <SI.icon size={12} color={SI.color}/> {s.replace('_',' ')}
              <span style={{ fontSize:10, opacity:0.8 }}>{n}</span>
            </button>
          );
        })}
        <span aria-hidden style={{ width:1, height:18, background:'var(--border)', margin:'0 2px' }} />
        {PRIORITA.slice().reverse().map(p => {
          const active = fPriorita === p;
          return (
            <button key={p} onClick={() => setFPriorita(active ? '' : p)} title={`Priorità ${p}`}
              style={{ fontSize:11.5, fontWeight:600, padding:'6px 11px', borderRadius:999, cursor:'pointer', touchAction:'manipulation',
                border:`1px solid ${active ? (PRI_STYLE[p].color) : 'var(--border)'}`,
                ...(active ? PRI_STYLE[p] : { background:'var(--surface)', color:'var(--text-muted)' }) }}>
              {p}
            </button>
          );
        })}
        {filtriAttivi && (
          <button onClick={() => { setFStato(''); setFPriorita(''); setFTesto(''); }}
            style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11.5, padding:'6px 10px', borderRadius:999, border:'1px solid var(--border)', background:'transparent', color:'var(--text-dim)', cursor:'pointer' }}>
            <X size={12}/> Pulisci
          </button>
        )}
        </div>
      </div>

      {/* Day selector — nascosto in vista "Tutte" o Kanban */}
      {!vistatutte && !vistaKanban && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 20 }}>
          {days.map(d => {
            const count = visibleTasks.filter(t => String(t.data_scadenza) === d).length;
            const done  = visibleTasks.filter(t => String(t.data_scadenza) === d && t.stato === 'completata').length;
            const isSel = d === selectedDay;
            const hasTask = count > 0;
            const allDone = count > 0 && done === count;
            return (
              <button key={d} onClick={() => setSelectedDay(d)} style={{
                flexShrink: 0, padding: '8px 14px', borderRadius: 10, height: 52,
                border: isSel ? '1px solid var(--cyan)' : '1px solid var(--border)',
                background: isSel ? 'var(--cyan-dim)' : 'var(--surface)',
                color: isSel ? 'var(--cyan)' : hasTask ? 'var(--text)' : 'var(--text-dim)',
                cursor: 'pointer', textAlign: 'center', transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                minWidth: 80, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:3, touchAction:'manipulation',
              }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.borderColor='var(--border-h)'; }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.borderColor='var(--border)'; }}>
                <div style={{ fontSize: 12, fontWeight: hasTask || isSel ? 700 : 500, lineHeight:1 }}>{dayLabel(d)}</div>
                {count > 0 ? (
                  <span style={{ fontSize:10, fontWeight:600, lineHeight:1, padding:'2px 7px', borderRadius:999,
                    background: isSel ? 'rgba(0,212,255,0.18)' : allDone ? 'var(--green-dim)' : 'var(--bg3)',
                    color: isSel ? 'var(--cyan)' : allDone ? 'var(--green)' : 'var(--text-muted)' }}>
                    {done}/{count}
                  </span>
                ) : (
                  <span style={{ fontSize:10.5, color:'var(--text-dim)', opacity:0.6, lineHeight:1 }}>—</span>
                )}
              </button>
              );
          })}
        </div>
      )}

      {/* New task form */}
      {showForm && (
        <CreateTaskForm
          form={form} setForm={setForm}
          clienti={clienti} utenti={utenti}
          onSubmit={submit} onClose={() => setShowForm(false)}
          loading={loadingForm}
          isOperatore={isOperatore}
        />
      )}

      {/* Task list / Kanban */}
      {vistaKanban ? (
        <KanbanBoard
          tasks={visibleTasks}
          onSelect={setSelectedTask}
          onToggle={toggleStato}
          onDelete={deleteTask}
          colorBorder={colorBorder}
          isOperatore={isOperatore}
        />
      ) : (
        <TaskList
          tasks={dayTasks}
          vistatutte={vistatutte}
          selectedDay={selectedDay}
          isOperatore={isOperatore}
          colorBorder={colorBorder}
          onSelect={setSelectedTask}
          onToggle={toggleStato}
          onDelete={deleteTask}
          totalTasks={tasks.length}
        />
      )}

      {/* Detail Panel */}
      {selectedTask && (
        <TaskPanel
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleUpdate}
          clienti={clienti}
          utenti={utenti}
          isOperatore={isOperatore}
          // Gli operatori possono modificare le task proprie, non assegnate, o CREATE da loro;
          // sola lettura solo se la task è di un altro e non l'hanno creata.
          readOnly={isOperatore && !(
            selectedTask.assegnata_a == null
            || String(selectedTask.assegnata_a) === String(user?.id)
            || String(selectedTask.creato_da) === String(user?.id)
          )}
        />
      )}
    </div>
  );
}
